import { Fragment } from "react";

import Mark from "@/Components/Icons/Mark";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";
import { ToolTipContainer } from "./TooltipContainer";
import { HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import { useTheme } from "@mui/material";
import { ExtendedConfigData } from "@/hooks/useConfig/types";

interface LineChartTooltipProps {
  date: string;
  rollingROI: number | null | undefined;
  totalRevenue: number;
  totalSpend: number;
  config: ExtendedConfigData;
}

export const LineChartTooltip: React.FC<LineChartTooltipProps> = ({
  date,
  rollingROI,
  totalRevenue,
  totalSpend,
  config,
}) => {
  const theme = useTheme();
  const { formatCurrency } = config.currency;
  const { revenueLabel, showReturnInsteadOfRevenue } = config.config;
  const allThreeSectionsShown = rollingROI != null && totalRevenue > 0 && totalSpend > 0;
  const shouldShowDivider = !allThreeSectionsShown;

  const revenueFormatterOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;

  return (
    <ToolTipContainer>
      <Typography fontWeight={600} mb={1}>
        {date}
      </Typography>
      <Divider variant="fullWidth" sx={{ paddingY: "0.2rem" }} />
      {rollingROI != null && (
        <Fragment>
          <Typography variant="body2" marginTop={2}>
            <Stack direction="row" alignItems="left" justifyContent="left" spacing={1}>
              <Mark markColor={theme.palette.ROILineChart.lineColor} />
              <Typography>
                <BoldSpan>Rolling ROI: </BoldSpan> {rollingROI}%
              </Typography>
            </Stack>
          </Typography>
        </Fragment>
      )}
      {totalRevenue > 0 && (
        <Fragment>
          {shouldShowDivider && <Divider variant="fullWidth" />}
          <Fragment>
            <Typography variant="body2" marginTop={2}>
              <Stack direction="row" alignItems="left" justifyContent="left" spacing={1}>
                <Mark markColor={theme.palette.RevenueLineChart.lineColor} />
                <Typography>
                  <BoldSpan>{revenueLabel}: </BoldSpan>{" "}
                  {formatCurrency(totalRevenue, revenueFormatterOptions)}
                </Typography>
              </Stack>
            </Typography>
          </Fragment>
        </Fragment>
      )}
      {totalSpend > 0 && (
        <Fragment>
          {shouldShowDivider && <Divider variant="fullWidth" />}
          <Fragment>
            <Typography variant="body2" marginTop={2}>
              <Stack direction="row" alignItems="left" justifyContent="left" spacing={1}>
                <Mark markColor={theme.palette.SpendLineChart.lineColor} />
                <Typography>
                  <BoldSpan>Total Spend: </BoldSpan> {formatCurrency(totalSpend, HIDE_DECIMAL)}
                </Typography>
              </Stack>
            </Typography>
          </Fragment>
        </Fragment>
      )}
    </ToolTipContainer>
  );
};
export default LineChartTooltip;
